import {
	fromCallback, fromPromise,
} from 'xstate';
import {
	type CreateLocalPhotoOutput,
	type ProcessUploadedPhotoOutput,
	type UploadError,
	type UploadItemActorInput,
	type UploadItemContext,
	type UploadItemEvent,
} from './machines';

export type UploadFileAdapterLifecycle = {
	abortSignal: AbortSignal;
	reportProgress: (uploadedBytes: number, totalBytes: number) => void;
	complete: (uploadUrl: string) => void;
	fail: (error: UploadError) => void;
};

export type UploadFileAdapterCleanup = () => void;

export type UploadFileAdapterResult =
	| void
	| UploadFileAdapterCleanup
	| Promise<void | UploadFileAdapterCleanup>;

export type UploadItemAdapters = {
	createLocalPhoto: (input: UploadItemContext) => CreateLocalPhotoOutput | Promise<CreateLocalPhotoOutput>;
	uploadFile: (input: UploadItemContext, lifecycle: UploadFileAdapterLifecycle) => UploadFileAdapterResult;
	processUploadedPhoto: (
		input: UploadItemContext,
	) => ProcessUploadedPhotoOutput | Promise<ProcessUploadedPhotoOutput>;
};

export function getUploadErrorRetryable(error: unknown): boolean {
	return (
		error instanceof Error
		&& 'retryable' in error
		&& typeof (error as { retryable?: unknown }).retryable === 'boolean'
	)
		? (error as { retryable: boolean }).retryable
		: true;
}

export function createUploadItemActors(adapters: UploadItemAdapters) {
	return {
		createLocalPhoto: fromPromise(
			async ({ input }: { input: UploadItemActorInput }): Promise<CreateLocalPhotoOutput> => (
				adapters.createLocalPhoto(input)
			),
		),
		uploadFile: fromCallback<UploadItemEvent, UploadItemActorInput>(({
			input,
			sendBack,
		}) => {
			const abortController = new AbortController();
			let cleanup: UploadFileAdapterCleanup | null = null;
			let stopped = false;

			const lifecycle: UploadFileAdapterLifecycle = {
				abortSignal: abortController.signal,
				reportProgress: (uploadedBytes, totalBytes) => {
					if (stopped) {
						return;
					}

					sendBack({
						type: 'upload.progress',
						uploadedBytes,
						totalBytes,
					});
				},
				complete: (uploadUrl) => {
					if (stopped) {
						return;
					}

					sendBack({
						type: 'upload.completed',
						uploadUrl,
					});
				},
				fail: (error) => {
					if (stopped) {
						return;
					}

					sendBack({
						type: 'upload.failed',
						error,
					});
				},
			};

			const failUpload = (error: unknown) => {
				if (stopped || abortController.signal.aborted) {
					return;
				}

				/*
				 * Upload errors default to retryable=true (network blips, S3
				 * throttling, etc.). Adapters that throw a deterministic failure
				 * (bucket gone, permanently expired signed url) attach
				 * `retryable: false` to the Error instance so we honor that — this
				 * mirrors setThrownFailure's handling of processing errors so a
				 * deterministic failure does not needlessly burn the retry budget.
				 */
				sendBack({
					type: 'upload.failed',
					error: {
						kind: 'upload',
						message: error instanceof Error ? error.message : String(error),
						retryable: getUploadErrorRetryable(error),
					},
				});
			};

			let adapterResult: UploadFileAdapterResult;

			try {
				adapterResult = adapters.uploadFile(
					input,
					lifecycle,
				);
			} catch (error: unknown) {
				failUpload(error);

				/*
				 * Identical disposer to the normal-return path below: stop further
				 * lifecycle events, abort the adapter's signal, and run any cleanup
				 * it managed to register. A synchronous throw means `cleanup` is
				 * still null here, but keeping both disposers in lockstep avoids the
				 * fragility of two diverging teardown paths.
				 */
				return () => {
					stopped = true;
					abortController.abort();
					cleanup?.();
				};
			}

			Promise.resolve(adapterResult)
				.then((adapterCleanup) => {
					if (typeof adapterCleanup !== 'function') {
						return;
					}

					/*
					 * The actor may have been disposed before this async cleanup
					 * resolved. In that case the disposer below already ran (so it
					 * could not see `cleanup`), and we must invoke the late cleanup
					 * ourselves. Otherwise stash it for the disposer to call.
					 */
					if (stopped) {
						adapterCleanup();
						return;
					}

					cleanup = adapterCleanup;
				})
				.catch(failUpload);

			/*
			 * Disposer: set `stopped` first so any in-flight lifecycle callbacks
			 * and the late-cleanup branch above turn into no-ops / self-cleanup,
			 * then abort the adapter's signal and run whatever cleanup it
			 * registered synchronously.
			 */
			return () => {
				stopped = true;
				abortController.abort();
				cleanup?.();
			};
		}),
		processUploadedPhoto: fromPromise(
			async ({ input }: { input: UploadItemActorInput }): Promise<ProcessUploadedPhotoOutput> => (
				adapters.processUploadedPhoto(input)
			),
		),
	};
}
